import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useNavigate } from 'react-router-dom';
import { Button, notification, Table, Tag, TreeSelect } from 'antd';
import {
  DownloadOutlined,
  EditOutlined,
  // PlusOutlined,
  QrcodeOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { PageContainer } from '@ant-design/pro-layout';
import { AuthContext } from 'context';
import branchService from 'services/branch.service';
import CheckAuth from 'components/common/CheckAuth';
import authorities from 'helpers/authorities';
import 'antd/dist/reset.css';
import download from 'js-file-download';
import dayjs from 'dayjs';
import { BranchContext } from '../../context';

const approvalStatusData = [
  {
    title: 'Yok',
    value: 'Yok',
  },
  {
    title: 'Kapatma Onayı Bekliyor',
    value: 'Kapatma Onayı Bekliyor',
  },
  {
    title: 'Açma Onayı Bekliyor',
    value: 'Açma Onayı Bekliyor',
  },
  {
    title: 'Vendor Onayı Bekliyor',
    value: 'Vendor Onayı Bekliyor',
  },
];

const FilterDropdown = ({ setSelectedKeys, selectedKeys, confirm, clearFilters, treeData }) => (
  <div style={{ padding: 8 }}>
    <TreeSelect
      treeData={treeData}
      placeholder="Ara"
      dropdownStyle={{ height: 200 }}
      value={selectedKeys}
      treeCheckable
      onChange={(value) => setSelectedKeys(value)}
      style={{ width: 200, marginBottom: 8, display: 'block' }}
    />
    <Button
      type="primary"
      onClick={() => confirm()}
      icon={<SearchOutlined />}
      size="small"
      style={{ width: 90, marginRight: 8 }}
    >
      Ara
    </Button>
    <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
      Temizle
    </Button>
  </div>
);

FilterDropdown.propTypes = {
  setSelectedKeys: PropTypes.func.isRequired,
  selectedKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  confirm: PropTypes.func.isRequired,
  clearFilters: PropTypes.func.isRequired,
  treeData: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

const RenderActions = ({ id, record, selectBranch, navigate }) => (
  <>
    <NavLink to={`/terminal-bilgileri/${id}`}>
      <Button className="mr-3" type="primary">
        <QrcodeOutlined />
        <span>Terminaller</span>
      </Button>
    </NavLink>

    <CheckAuth authorityCode={authorities.UPDATE_BRANCH}>
      <NavLink to="/sube-islemleri/edit">
        <Button
          type="ghost"
          onClick={() => {
            selectBranch(record);
            navigate('/sube-islemleri/edit');
          }}
        >
          <EditOutlined />
        </Button>
      </NavLink>
    </CheckAuth>
  </>
);

RenderActions.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  record: PropTypes.shape({
    BranchId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    BranchTitle: PropTypes.string,
    ContactName: PropTypes.string,
    Telephone: PropTypes.string,
    cityName: PropTypes.string,
    districtName: PropTypes.string,
    neighborhoodName: PropTypes.string,
    Address: PropTypes.string,
    pendingApprovalName: PropTypes.string,
    identifier: PropTypes.string,
  }).isRequired,
  selectBranch: PropTypes.func.isRequired,
  navigate: PropTypes.func.isRequired,
};

const getFilterDropdown = (treeData) => {
  // eslint-disable-next-line react/prop-types
  return ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <FilterDropdown
      setSelectedKeys={setSelectedKeys}
      selectedKeys={selectedKeys}
      confirm={confirm}
      clearFilters={clearFilters}
      treeData={treeData}
    />
  );
};

const Branch = () => {
  const navigate = useNavigate();
  const context = useContext(AuthContext);
  const { selectBranch } = useContext(BranchContext);
  const number = context.selectedMerch?.number;

  const [branches, setBranches] = useState([]);

  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        if (number) {
          const { payload } = await branchService.getBranchesByMerch(number);
          setLoading(false);
          setBranches(payload);
        }
      } catch (error) {
        setLoading(false);
        notification.error({
          message: 'İşlem Başarısız',
          description: error,
        });
      }
    })();
  }, [number]);

  const downloadAllExcel = async () => {
    try {
      setDownloading(true);
      const data = await branchService.getBranchReportsExcel(number);
      download(data, `SubeIslemleri-${number}-${dayjs().format('DD-MM-YYYY')}.xls`);
      setDownloading(false);
    } catch (error) {
      setDownloading(false);
      notification.error({
        message: 'İşlem Başarısız',
        description: error,
      });
    }
  };

  const columns = [
    {
      title: <div className="table-title-calibri-bold-12">Şube No</div>,
      dataIndex: 'BranchId',
      key: 'BranchId',
      align: 'center',
      width: 100,
      render: (text, row) => {
        return (
          <span className="table-title-calibri-bold-12">{row.BranchId ? row.BranchId : ''}</span>
        );
      },
    },
    {
      title: <div className="table-title-calibri-bold-12">Şube Adı</div>,
      dataIndex: 'BranchTitle',
      key: 'BranchTitle',
      align: 'center',
      width: 100,
      // filters: branches.map(item => ({ text: item.BranchTitle, value: item.BranchTitle })),
      onFilter: (value, record) => record.BranchTitle.indexOf(value) === 0,
      filterDropdown: getFilterDropdown(
        branches.map((item) => ({ title: item.BranchTitle, value: item.BranchTitle })),
      ),
      render: (text, row) => {
        return (
          <span className="table-title-calibri-bold-12" style={{ textAlign: 'center' }}>
            {row.BranchTitle ? row.BranchTitle : ''}
          </span>
        );
      },
    },
    {
      title: <div className="table-title-calibri-bold-12">Şube Yetkilisi</div>,
      dataIndex: 'ContactName',
      key: 'ContactName',
      align: 'center',
      width: 100,
      render: (text, row) => {
        return (
          <span className="table-title-calibri-bold-12">
            {row.ContactName ? row.ContactName : ''}
          </span>
        );
      },
    },
    {
      title: <div className="table-title-calibri-bold-12">Telefon</div>,
      dataIndex: 'Telephone',
      key: 'Telephone',
      width: 90,
      render: (text, row) => {
        return (
          <span className="table-title-calibri-bold-12">{row.Telephone ? row.Telephone : ''}</span>
        );
      },
    },
    {
      title: <div className="table-title-calibri-bold-12">İl</div>,
      dataIndex: 'cityName',
      key: 'cityName',
      align: 'center',
      width: 140,
      render: (text, row) => {
        return (
          <span className="table-title-calibri-bold-12">{row.cityName ? row.cityName : ''}</span>
        );
      },
    },
    {
      title: <div className="table-title-calibri-bold-12">İlçe</div>,
      dataIndex: 'districtName',
      key: 'districtName',
      width: 70,
      render: (text, row) => {
        return (
          <span className="table-title-calibri-bold-12">
            {row.districtName ? row.districtName : ''}
          </span>
        );
      },
    },
    {
      title: <div className="table-title-calibri-bold-12">Mahalle</div>,
      dataIndex: 'neighborhoodName',
      key: 'neighborhoodName',
      width: 100,
      render: (text, row) => {
        return (
          <span className="table-title-calibri-bold-12">
            {row.neighborhoodName ? row.neighborhoodName : ''}
          </span>
        );
      },
    },
    {
      title: <div className="table-title-calibri-bold-12">Adres</div>,
      dataIndex: 'Address',
      key: 'Address',
      align: 'center',
      width: 200,
      render: (text, row) => {
        return (
          <div style={{ textAlign: 'center' }}>
            <span className="table-title-calibri-bold-12">{row.Address ? row.Address : ''}</span>
          </div>
        );
      },
    },
    {
      title: <div className="table-title-calibri-bold-12">Onay Durumu</div>,
      dataIndex: 'pendingApprovalName',
      key: 'pendingApprovalName',
      align: 'center',
      width: 100,
      onFilter: (value, record) => record.pendingApprovalName.indexOf(value) === 0,
      filterDropdown: getFilterDropdown(approvalStatusData),
      render: (pendingApprovalName, record) => {
        return (
          <Tag color="volcano" key={record?.identifier} className="table-title-calibri-bold-12">
            {pendingApprovalName}
          </Tag>
        );
      },
    },
    {
      title: <div className="table-title-calibri-bold-12">İşlemler</div>,
      dataIndex: 'BranchId',
      key: 'actions',
      align: 'center',
      className: 'actions',
      fixed: 'right',
      width: 200,
      render: (id, record) => (
        <RenderActions id={id} record={record} selectBranch={selectBranch} navigate={navigate} />
      ),
    },
  ];

  return (
    <PageContainer
      title="Şube İşlemleri"
      subTitle={`${number} vergi numaralı iş yerine ait şubeler listelenmektedir.`}
      extra={[
        // <CheckAuth key="new-branch" authorityCode={authorities.ADD_BRANCH}>
        //   <Button
        //     key="1"
        //     type="primary"
        //     icon={<PlusOutlined />}
        //     onClick={() => navigate('/basvurular/yeni-sube-basvurusu')}
        //   >
        //     Yeni Şube Başvurusu
        //   </Button>
        // </CheckAuth>,

        <Button
          loading={downloading}
          disabled={loading || downloading}
          key="downloadAll"
          type="primary"
          icon={<DownloadOutlined />}
          onClick={async () => {
            await downloadAllExcel();
          }}
        >
          Excel İndir
        </Button>,
      ]}
    >
      <Table
        loading={loading}
        dataSource={branches}
        columns={columns}
        pagination={false}
        rowKey="BranchTitle"
        scroll={{ x: true }}
      />
    </PageContainer>
  );
};

export default Branch;
